import { ChangeEvent, RefObject, useEffect, useRef } from "react";
import { usePrevious } from "react-use";

interface Params {
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  value?: string;
  shouldMaskValue?: boolean;
  maskedValue: string;
}

export const getHandleOnChange =
  ({ onChange, value, shouldMaskValue, maskedValue }: Params) =>
  (e: ChangeEvent<HTMLInputElement>) => {
    if (!shouldMaskValue) {
      onChange(e);
      return;
    }

    const newMaskedValue = e.target.value;
    const selectionStart = e.target.selectionStart ?? 0;

    const currentValue = value ?? "";
    const lengthDiff = newMaskedValue.length - maskedValue.length;
    let updatedValue = currentValue;

    if (lengthDiff > 0) {
      const inserted = newMaskedValue.substring(
        selectionStart - lengthDiff,
        selectionStart
      );

      // Insert the new characters at the correct position in the actual value
      const insertPosition = Math.min(
        selectionStart - lengthDiff,
        currentValue.length
      );

      updatedValue =
        currentValue.substring(0, insertPosition) +
        inserted +
        currentValue.substring(insertPosition);
    } else if (lengthDiff < 0) {
      // Calculate how many characters were deleted
      const deleteCount = Math.abs(lengthDiff);

      updatedValue =
        currentValue.substring(0, selectionStart) +
        currentValue.substring(selectionStart + deleteCount);
    }

    const modifiedEvent = {
      ...e,
      target: {
        ...e.target,
        value: updatedValue,
      },
    } as ChangeEvent<HTMLInputElement>;

    // Send the event up to parent
    onChange(modifiedEvent);
  };

interface UseMaskValueChangeHandlerParams {
  value?: string;
  shouldMaskValue?: boolean;
  setMaskedValue: (maskedValue: string) => void;
  cursorPositionRef: RefObject<number>;
}

const maskString = (str: string): string => str.replace(/\S/g, "•");

const getMaskedValueForCursorAtEnd = (value: string): string => {
  const lastChar = value.charAt(value.length - 1);
  return maskString(value.slice(0, -1)) + lastChar;
};

const getMaskedValueForCursorInMiddle = (
  value: string,
  cursorPosition: number
): string => {
  let charPosition = cursorPosition - 1;
  let newChar = value.charAt(charPosition);

  // If it's a space and not the last character, use the next character instead
  if (newChar === " " && charPosition < value.length - 1) {
    charPosition++;
    newChar = value.charAt(charPosition);
  }

  return (
    maskString(value.slice(0, charPosition)) +
    newChar +
    maskString(value.slice(charPosition + 1))
  );
};

const createMaskedValue = (
  value: string,
  cursorPosition: number | null | undefined
): string => {
  if (value.length === 0) {
    return "";
  }

  if (!cursorPosition || cursorPosition === value.length) {
    return getMaskedValueForCursorAtEnd(value);
  } else {
    return getMaskedValueForCursorInMiddle(value, cursorPosition);
  }
};

export const useMaskValueChangeHandler = ({
  value,
  shouldMaskValue,
  setMaskedValue,
  cursorPositionRef,
}: UseMaskValueChangeHandlerParams) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const prevShouldMaskValueRef = usePrevious<boolean | undefined>(
    shouldMaskValue
  );
  // Track initial render to handle edge case in tests
  const isInitialRender = useRef(true);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!value) {
      setMaskedValue("");
    } else if (!shouldMaskValue) {
      setMaskedValue(value);
    } else {
      const updatedMaskedValue = createMaskedValue(
        value,
        cursorPositionRef.current
      );
      // If consumer is switching back and forth for show/hide, don't show the character briefly
      if (
        !isInitialRender.current &&
        prevShouldMaskValueRef !== shouldMaskValue
      ) {
        setMaskedValue(maskString(value));
        return;
      }

      setMaskedValue(updatedMaskedValue);

      timerRef.current = setTimeout(() => {
        setMaskedValue(maskString(value));
      }, 500);
    }

    isInitialRender.current = false;

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [value, shouldMaskValue]);
};
